import React, {useRef, useMemo} from 'react';
import './Person.css'
import config from '../../config.json';
import { useSelector, useDispatch } from 'react-redux';
import { showPlanForm, createPlannedItem } from '../../store/PlannedItemStore';
import { checkIn, checkOut, showRadial } from '../../store/PersonStore';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
    selectUnansweredEventsForPerson,
    selectItemsTodayForPerson,
    selectCurrentItemForPerson,
    selectIsWinterDutyForPerson,
    selectColorCodeBySystemName,
} from '../../Selectors';


import checkInInvertedImage from '../../images/radial_checkin_inversed.png';
import checkOutInvertedImage from '../../images/radial_checkout_inversed.png';
import planInvertedImage from '../../images/radial_plan_inversed.png';
import checkInImage from '../../images/radial_checkin.png';
import checkOutImage from '../../images/radial_checkout.png';
import radialPlanImage from '../../images/radial_plan.png';
import illImage from '../../images/radial_ill.png';
import illInvertedImage from '../../images/radial_ill_inversed.png';
import flagImage from '../../images/flag.png';
import starImage from '../../images/notification.png';
import { useNavigate } from 'react-router-dom';

const Person = ({ person = {} }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const elementRef = useRef(null);

    const unansweredEvent = useSelector((state) => person?.id ? selectUnansweredEventsForPerson(state, person.id) : []);
    const itemsToday = useSelector((state) => person?.id ? selectItemsTodayForPerson(state, person.id) : []);
    const item = useSelector((state) => person?.id ? selectCurrentItemForPerson(state, person.id) : null);
    const isWinterDuty = useSelector((state) => person?.id ? selectIsWinterDutyForPerson(state, person.id) : false);
    const checkColor = useSelector((state) => selectColorCodeBySystemName(state, 'checked'));
    const uncheckColor = useSelector((state) => selectColorCodeBySystemName(state, 'unchecked'));
    const sickColor = useSelector((state) => selectColorCodeBySystemName(state, 'sick'));

    const checkInWithId = () => {
        dispatch(checkIn(person.id));
    };

    const checkOutWithId = () => {
        dispatch(checkOut(person.id));
    };

    const plan = () => {
        navigate(`/plan/${person.id}`);
        dispatch(showPlanForm(true));
    };

    const planSick = () => {
        const start = moment().set('hour', 7).set('minutes', 0).valueOf();
        const end = moment().set('hour', 15).set('minutes', 0).valueOf();

        dispatch(
            createPlannedItem({
                personId: person.id,
                colorCodeId: sickColor.id,
                startTime: start,
                endTime: end,
            })
        );
    };

    const toggleRadialMenu = () => {
        dispatch(showRadial({ personId: person.id, show: !person.showRadial }));
    };

    const RadialMenu = () => {
        if (!person.showRadial || !elementRef.current) return null;

        const pct = elementRef.current.getBoundingClientRect().left / document.documentElement.clientWidth;
        const inverse = pct > 0.5;

        return (
            <div className={`radial${inverse ? ' inversed' : ''}`}>
                <div className="checkin">
                    <img src={inverse ? checkInInvertedImage : checkInImage} alt="checkin"/>
                    <button onClick={checkInWithId}>
                        {checkColor && checkColor.name}
                    </button>
                </div>
                <div className="checkout">
                    <img src={inverse ? checkOutInvertedImage : checkOutImage} alt="checkout"/>
                    <button onClick={checkOutWithId}>
                        {uncheckColor && uncheckColor.name}
                    </button>
                </div>

                <div className="plan">
                    <img src={inverse ? planInvertedImage : radialPlanImage} alt="plan"/>
                    <button onClick={plan}>
                        Planlæg
                    </button>
                </div>

                <div className="ill">
                    <img src={inverse ? illInvertedImage : illImage}  alt='illness'/>
                    <button onClick={planSick}>
                        Syg i dag
                    </button>
                </div>
            </div>
        );
    };

    let className = 'person clearfix';
    let style;

    if(person.status === 'checked'){
        className += ' circle checked';
        style = { borderColor:`${checkColor && checkColor.hexColor}`, borderRadius: '50%', borderStyle: 'solid'};
    }
    if(person.status === 'unchecked'){
        className += ' circle unchecked';
        style = { borderColor:`${uncheckColor && uncheckColor.hexColor}`, borderRadius: '50%', borderStyle: 'solid'};
    }
    className = person.showRadial ? className + ' circle radial' : className;

    if(item){
        className += ' circle';
        style = { borderColor:`${item.colorCode.hexColor}`, borderRadius: '50%', borderStyle: 'solid'};
    }

    const isLoading = false;
    const profileImageClass = isLoading ? 'profile-img loading' : 'profile-img';

    const birthDate = person.birthDate ? moment(person.birthDate) : null;
    const isB = birthDate && birthDate.isSame(moment().year(birthDate.year()), 'd');
    const celebrateComp = isB ? <img className="birthday" src={flagImage} alt=''/> : '';
    const winter = isWinterDuty ? <svg className="svg-inline--fa fa-snowflake fa-w-14 winter" aria-hidden="true" data-prefix="fas" data-icon="snowflake" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512" data-fa-i2svg=""><path fill="currentColor" d="M444.816 301.639a24.12 24.12 0 0 0 2.661-16.978c-2.725-12.966-15.339-21.245-28.174-18.492l-87.407 25.046L264 256l67.896-35.215 87.407 25.046c12.835 2.753 25.449-5.526 28.174-18.492 2.725-12.966-5.471-25.708-18.305-28.461l-47.477-7.137 53.077-30.956c11.363-6.627 15.257-21.306 8.696-32.785-6.561-11.479-21.091-15.412-32.454-8.785l-53.077 30.956 17.621-45.104c4.057-12.606-2.768-26.146-15.247-30.245-12.478-4.099-25.883 2.797-29.94 15.402l-22.232 88.99-60.38 35.215V144l65.175-63.945c8.778-9.852 7.987-25.027-1.766-33.894-9.753-8.867-24.775-8.068-33.552 1.784l-29.857 37.967V24c0-13.255-10.637-24-23.758-24s-23.758 10.745-23.758 24v61.912l-29.857-37.967c-8.779-9.852-23.799-10.652-33.552-1.784-9.753 8.867-10.543 24.042-1.766 33.894L200.242 144v70.431l-60.38-35.215-22.232-88.99c-4.057-12.605-17.462-19.501-29.94-15.402-12.478 4.099-19.304 17.64-15.247 30.245l17.62 45.104-53.077-30.956c-11.363-6.627-25.893-2.694-32.454 8.785s-2.667 26.157 8.696 32.785l53.077 30.956-47.477 7.137C5.993 201.634-2.203 214.375.523 227.341c2.725 12.965 15.339 21.245 28.174 18.492l87.407-25.046L184 256l-67.896 35.215-87.406-25.045c-12.835-2.753-25.449 5.526-28.174 18.492-2.725 12.967 5.47 25.708 18.305 28.461l47.477 7.137-53.077 30.956C1.866 357.843-2.027 372.521 4.533 384s21.091 15.412 32.454 8.785l53.077-30.956-17.62 45.104a24.157 24.157 0 0 0 2.022 19.428c2.831 4.953 7.416 8.909 13.224 10.816 12.478 4.099 25.883-2.797 29.94-15.402l22.232-88.99 60.38-35.215V368l-65.175 63.945c-8.778 9.852-7.987 25.027 1.766 33.894 9.754 8.868 24.774 8.068 33.552-1.784l29.857-37.967V488c0 13.255 10.637 24 23.758 24s23.758-10.745 23.758-24v-61.912l29.857 37.967A23.59 23.59 0 0 0 295.282 472a23.534 23.534 0 0 0 15.885-6.161c9.753-8.867 10.544-24.042 1.766-33.894L247.758 368v-70.431l60.38 35.215 22.232 88.99c4.057 12.605 17.462 19.501 29.94 15.402 12.479-4.099 19.304-17.64 15.247-30.245l-17.621-45.104 53.077 30.956c11.363 6.627 25.893 2.694 32.454-8.785s2.667-26.157-8.696-32.785l-53.077-30.956 47.477-7.137c6.86-1.469 12.394-5.793 15.645-11.481z"></path></svg> : '';

    return (
        <div className={className} onClick={toggleRadialMenu}>
            <div ref={elementRef} className="profile clearfix" >
                                    {person.statusChange && checkColor && (person.status === "checked" || person.status === "unchecked") &&
                        <div className='check-time' style={{background: person.status === "checked" ? checkColor.hexColor : uncheckColor.hexColor}}>{moment(person.statusChange).format("HH:mm")}</div>
                    }
                <img className={profileImageClass} src={config.server + person.imagePath} alt="" style={style ? style : {}}/>
                {unansweredEvent.length > 0 && <div className='profile-img-star'><img src={starImage} alt={'event_invite'}/><span className='event-count'>{unansweredEvent.length}</span></div> }
                {celebrateComp}
                {winter}
                {RadialMenu()}
            </div>
            <div className="flytext">
                <div>{person.name}</div>
                <div>{person.title}</div>
                <div>{person.phoneNumber}</div>
            </div>
            <div className="flytext-plans">
                {itemsToday &&
                    itemsToday.map((x,i) => {
                            if(item && (x.id === item.id)) return null; // hvis x's colorCodeId svarer til .systemCode == 'sick'
                            return(
                                <div key={i} className='plan'>
                                    {moment(x.startTime).format('HH:mm')}
                                    <span style={{backgroundColor: x.colorCode.hexColor}} className='dot'/>
                                </div>
                            )
                        }
                    )
                }
            </div>
        </div>
    );
}

Person.propTypes = {
    person: PropTypes.object
};

export default Person;